// A small guitar chord fingering diagram. frets[] is 6 values, low E → high E:
//   -1 = muted (×), 0 = open (○), n = fretted at fret n.
import type { JSX } from "react";   // React 19: the JSX namespace is no longer global

export function ChordDiagram({ frets, label }: { frets: number[]; label: string }) {
  const NS = 6, NF = 4;            // 6 strings, 4-fret window
  const fr = frets.filter((f) => f > 0);
  const maxF = fr.length ? Math.max(...fr) : 0;
  const base = maxF > NF ? Math.min(...fr) : 1;   // window start (barre chords show the start fret)
  const showNut = base === 1;
  const W = 58, H = 76, L = 9, R = 9, T = 18, GH = 44;
  const G = 10;                    // side gutters: the fret-position number lives in the left one
  const sx = (i: number) => G + L + ((W - L - R) / (NS - 1)) * i;
  const fy = (row: number) => T + (GH / NF) * row;
  const rowMidY = fy(0) + GH / NF / 2;   // vertical center of the top fret row

  const marks: JSX.Element[] = [];
  const dots: JSX.Element[] = [];
  frets.forEach((f, i) => {
    const x = sx(i);
    if (f < 0) marks.push(<text key={"m" + i} x={x} y={T - 5} className="cd-mark">×</text>);
    else if (f === 0) marks.push(<text key={"o" + i} x={x} y={T - 5} className="cd-mark">○</text>);
    else {
      const row = f - base + 1;
      if (row >= 1 && row <= NF) dots.push(<circle key={"d" + i} cx={x} cy={fy(row) - GH / NF / 2} r={3.6} className="cd-dot" />);
    }
  });

  return (
    <div className="chord-dia" title={label}>
      <svg viewBox={`0 0 ${W + 2 * G} ${H}`} width={W + 2 * G} height={H} aria-hidden="true">
        {Array.from({ length: NS }, (_, i) => (
          <line key={"s" + i} x1={sx(i)} y1={T} x2={sx(i)} y2={T + GH} className="cd-line" />
        ))}
        {Array.from({ length: NF + 1 }, (_, r) => (
          <line key={"f" + r} x1={G + L} y1={fy(r)} x2={G + W - R} y2={fy(r)} className={"cd-line" + (showNut && r === 0 ? " cd-nut" : "")} />
        ))}
        {marks}
        {dots}
        {/* barre/movable chords: the start fret, aligned to its row just left of the grid */}
        {!showNut && (
          <text x={G + L - 6} y={rowMidY} className="cd-pos" textAnchor="end" dominantBaseline="central">{base}</text>
        )}
      </svg>
      <div className="cd-name">{label}</div>
    </div>
  );
}
